import { memo, FC, useState, useEffect } from 'react'

import { Category, Service, TCFData } from '@usercentrics/cmp-browser-sdk'
import { Element } from 'react-scroll'

import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'

import { orientation } from '@/constants/focus'
import { privacySettingsTab } from '@/containers/Usercentrics/PrivacySettings'
import { PrivacyTcfSection } from '@/containers/Usercentrics/PrivacyTcfSection'
import { useCustomFocus } from '@/hooks/useCustomFocus'
import { PrivacySettingsTabContentProps } from '@/types/componentsPropsTypes'

import { PrivacyCategorySection } from './PrivacyCategorySection'
import { PrivacyServicesSection } from './PrivacyServicesSection'
import { TabContent } from './styles'
import {
  getCategoriesFullInfo,
  getServicesFullInfo,
  getTcfData,
} from './usercentricsUtils'

export enum TcfSettings {
  TCF_VENDORS = 'vendors',
  TCF_PURPOSE = 'purposes',
  TCF_SPECIAL_PURPOSE = 'specialPurposes',
  TCF_FEATURE = 'features',
  TCF_SPECIAL_FEATURE = 'specialFeatures',
}

const PrivacySettingsTabContentRaw: FC<PrivacySettingsTabContentProps> = ({
  isActiveTabContent,
  tabContentType,
  tabTitle,
  servicesDecision,
  tcfUserDecisions,
  setServicesDecision,
  setTcfUserDecisions,
}) => {
  const focusId = `TabContent_${tabTitle}`
  const toggleSectionFocusId = `Toggle_section_${tabTitle}`
  const { defaultFocusChild, onMove } = useCustomFocus({ focusId })
  const [categoriesInfo, setCategoriesInfo] = useState<Array<Category>>([])
  const [servicesInfo, setServicesInfo] = useState<Array<Service>>([])
  const [tcfData, setTcfData] = useState<Nullable<TCFData>>()

  useEffect(() => {
    getCategoriesInfo()
    getServicesInfo()
    getTcfInfo()
  }, [])

  const getCategoriesInfo = async () => {
    try {
      const categoriesInfo = await getCategoriesFullInfo()
      setCategoriesInfo(categoriesInfo || [])
    } catch {
      isDevMode && console.warn('[Usercentrics] getCategoriesFullInfo error')
    }
  }

  const getServicesInfo = async () => {
    try {
      const servicesInfo = await getServicesFullInfo()
      setServicesInfo(servicesInfo || [])
    } catch {
      isDevMode && console.warn('[Usercentrics] getServicesFullInfo error')
    }
  }

  const getTcfInfo = () => {
    const tcfData = getTcfData()
    setTcfData(tcfData || null)
  }

  return (
    <TabContent
      focusId={focusId}
      $activeTab={isActiveTabContent}
      disabled={!isActiveTabContent}
      orientation={orientation.VERTICAL}
      defaultFocusChild={defaultFocusChild}
      onMove={onMove}
    >
      {tabContentType === privacySettingsTab.PURPOSES_TAB &&
        tcfData !== undefined && (
          <>
            {tcfData?.purposes.map((data) => {
              const focusId = `${toggleSectionFocusId}_${TcfSettings.TCF_PURPOSE}_${data.id}`
              const tcfUserDecision = tcfUserDecisions[
                TcfSettings.TCF_PURPOSE
              ]?.find(({ id }) => id === data.id)
              return (
                <Element key={focusId} name={focusId}>
                  <PrivacyTcfSection
                    data={data}
                    tabTitle={tabTitle}
                    tcfSettingsType={TcfSettings.TCF_PURPOSE}
                    tcfUserDecision={tcfUserDecision}
                    setTcfUserDecisions={setTcfUserDecisions}
                  />
                </Element>
              )
            })}
            {tcfData?.specialPurposes.map((data) => {
              const focusId = `${toggleSectionFocusId}_${TcfSettings.TCF_SPECIAL_PURPOSE}_${data.id}`
              return (
                <Element key={focusId} name={focusId}>
                  <PrivacyTcfSection
                    data={data}
                    tabTitle={tabTitle}
                    tcfSettingsType={TcfSettings.TCF_SPECIAL_PURPOSE}
                  />
                </Element>
              )
            })}
            {tcfData?.features.map((data) => {
              const focusId = `${toggleSectionFocusId}_${TcfSettings.TCF_FEATURE}_${data.id}`
              return (
                <Element key={focusId} name={focusId}>
                  <PrivacyTcfSection
                    data={data}
                    tabTitle={tabTitle}
                    tcfSettingsType={TcfSettings.TCF_FEATURE}
                  />
                </Element>
              )
            })}
            {tcfData?.specialFeatures.map((data) => {
              const focusId = `${toggleSectionFocusId}_${TcfSettings.TCF_SPECIAL_FEATURE}_${data.id}`
              const tcfUserDecision = tcfUserDecisions[
                TcfSettings.TCF_SPECIAL_FEATURE
              ]?.find(({ id }) => id === data.id)
              return (
                <Element key={focusId} name={focusId}>
                  <PrivacyTcfSection
                    data={data}
                    tabTitle={tabTitle}
                    tcfSettingsType={TcfSettings.TCF_SPECIAL_FEATURE}
                    tcfUserDecision={tcfUserDecision}
                    setTcfUserDecisions={setTcfUserDecisions}
                  />
                </Element>
              )
            })}
            {categoriesInfo.map((data) => {
              const focusId = `${toggleSectionFocusId}_${data.slug}`
              return (
                <Element key={focusId} name={focusId}>
                  <PrivacyCategorySection
                    data={data}
                    tabTitle={tabTitle}
                    disabled={!isActiveTabContent}
                    servicesDecision={servicesDecision}
                    setServicesDecision={setServicesDecision}
                  />
                </Element>
              )
            })}
          </>
        )}

      {tabContentType === privacySettingsTab.VENDOR_TAB &&
        tcfData !== undefined && (
          <>
            {tcfData?.vendors.map((data) => {
              const focusId = `${toggleSectionFocusId}_${TcfSettings.TCF_VENDORS}_${data.id}`
              const tcfUserDecision = tcfUserDecisions[
                TcfSettings.TCF_VENDORS
              ]?.find(({ id }) => id === data.id)
              return (
                <Element key={focusId} name={focusId}>
                  <PrivacyTcfSection
                    data={data}
                    tabTitle={tabTitle}
                    tcfSettingsType={TcfSettings.TCF_VENDORS}
                    tcfUserDecision={tcfUserDecision}
                    setTcfUserDecisions={setTcfUserDecisions}
                  />
                </Element>
              )
            })}
            {servicesInfo.map((data) => {
              const focusId = `${toggleSectionFocusId}_${data.id}`
              return (
                <Element key={focusId} name={focusId}>
                  <PrivacyServicesSection
                    data={data}
                    tabTitle={tabTitle}
                    servicesDecision={servicesDecision}
                    setServicesDecision={setServicesDecision}
                  />
                </Element>
              )
            })}
          </>
        )}
      {tabContentType === privacySettingsTab.CATEGORY_TAB &&
        categoriesInfo.map((data) => {
          const focusId = `${toggleSectionFocusId}_${data.slug}`
          return (
            <Element key={focusId} name={focusId}>
              <PrivacyCategorySection
                data={data}
                tabTitle={tabTitle}
                disabled={!isActiveTabContent}
                servicesDecision={servicesDecision}
                setServicesDecision={setServicesDecision}
              />
            </Element>
          )
        })}
      {tabContentType === privacySettingsTab.SERVICE_TAB &&
        servicesInfo.map((data) => {
          const focusId = `${toggleSectionFocusId}_${data.id}`
          return (
            <Element key={focusId} name={focusId}>
              <PrivacyServicesSection
                data={data}
                tabTitle={tabTitle}
                servicesDecision={servicesDecision}
                setServicesDecision={setServicesDecision}
              />
            </Element>
          )
        })}
    </TabContent>
  )
}

export const PrivacySettingsTabContent = memo(PrivacySettingsTabContentRaw)
